// The query string of /ricerca, read and written in one place, so the back
// button and a shared link give the same search:
//   q             the words, as typed (quotes, -, OR, * included)
//   mode          variants, names, exact: old links only (names is read as every word a name)
//                                               (default synonyms: no plurals, the synonyms and acronyms yes)
//   words         any: «Almeno una parola», phrase: «Frase esatta»   (default all: «Tutte le parole»)
//   variants      «Imposta plurali e varianti», word by word: the words with their plurals,
//                 comma-separated, folded (default none)
//   prefixes      «Scegli la variante», word by word: word:beginning (legge:legg), comma-separated
//   names         «Nome proprio», word by word: the words searched as a name, comma-separated, folded
//   nosyn         «Anche «disegno di legge»» taken off, word by word: the words without synonyms
//   (each word has one of these choices: «Impostazioni sulle singole parole», decision 34)
//   sort          oldest | relevance            (default recent: «Più recenti»)
//   period        7d | 1m | all | range         (default 6m); range with from and to (YYYY-MM-DD)
//   origin        orchestra | coro              (default: both, «Tutto»)
//   followed      1: «Solo le fonti che seguo»  (default off)
//   chamber       camera,senato                 (advanced, Parlamento only)
//   session_type  plenaria,commissioni          (advanced, Parlamento only)
//   resource      Coro's document types, comma-separated (advanced, Parlamento only)
//   (a value of the three advanced filters leaves the News out of the search: the api says so)
//   sources       origin:source_key, comma-separated; feed_id  one of the caller's feeds:
//                 no control on the page sets them, a saved search may carry them
// Pure functions over URLSearchParams and the contract's types, so the page,
// the saved searches and the tests share them.
import type { components, operations } from "../../api/schema";

export type SavedQuery = components["schemas"]["SavedQuery"];
export type SearchMode = SavedQuery["mode"];
export type SearchWords = SavedQuery["words"];
export type SearchSort = SavedQuery["sort"];
/** The periods a saved search keeps: relative to today. */
export type SearchPeriod = SavedQuery["period"];
/** The periods of the page: the relative ones and «Da… a…». */
export type PagePeriod = NonNullable<operations["search_v2"]["parameters"]["query"]["period"]>;
export type SearchOrigin = NonNullable<SavedQuery["origin"]>;
export type Chamber = NonNullable<SavedQuery["chamber"]>[number];
export type SessionType = NonNullable<SavedQuery["session_type"]>[number];
/** Coro's document types, the closed list of the contract. */
export type Resource = NonNullable<SavedQuery["resource"]>[number];
/** The query of GET /search/v2, as the client sends it. */
export type SearchQuery = operations["search_v2"]["parameters"]["query"];

export interface SearchParams {
  q: string;
  mode: SearchMode;
  words: SearchWords;
  /** The words of `q` that take their plurals and variants (folded: «bilancio»); empty: none. */
  variantsFor: string[];
  /** The words of `q` searched by a beginning of theirs, word → beginning (legge → legg), folded. */
  prefixFor: Record<string, string>;
  /** The words of `q` searched as a name («Conte» and «CONTE»), folded; next to each other, one person. */
  namesFor: string[];
  /** The words of `q` searched without the synonyms of the lexicon (ddl, not «disegno di legge»), folded. */
  noSynonymsFor: string[];
  sort: SearchSort;
  period: PagePeriod;
  /** The days of «Da… a…» (YYYY-MM-DD), both included; null unless period is range. */
  dateFrom: string | null;
  dateTo: string | null;
  /** null: News and Parlamento («Tutto»). */
  origin: SearchOrigin | null;
  followedOnly: boolean;
  /** null: no filter (an empty choice is no filter as well). */
  chamber: Chamber[] | null;
  sessionType: SessionType[] | null;
  resource: Resource[] | null;
  sources: string[] | null;
  feedId: string | null;
}

export const DEFAULT_SEARCH_PARAMS: SearchParams = {
  q: "",
  mode: "synonyms",
  words: "all",
  variantsFor: [],
  prefixFor: {},
  namesFor: [],
  noSynonymsFor: [],
  sort: "recent",
  period: "6m",
  dateFrom: null,
  dateTo: null,
  origin: null,
  followedOnly: false,
  chamber: null,
  sessionType: null,
  resource: null,
  sources: null,
  feedId: null,
};

/** Every value of the contract, in the order the page shows them: a new one fails the typecheck here. */
export const MODES = ["synonyms", "variants", "names", "exact"] as const satisfies readonly SearchMode[];
export const WORDS = ["all", "any", "phrase"] as const satisfies readonly SearchWords[];
export const SORTS = ["recent", "oldest", "relevance"] as const satisfies readonly SearchSort[];
export const PERIODS = ["7d", "1m", "6m", "all"] as const satisfies readonly SearchPeriod[];
export const PAGE_PERIODS = [...PERIODS, "range"] as const satisfies readonly PagePeriod[];
export const ORIGINS = ["orchestra", "coro"] as const satisfies readonly SearchOrigin[];
export const CHAMBERS = ["camera", "senato"] as const satisfies readonly Chamber[];
export const SESSION_TYPES = ["plenaria", "commissioni"] as const satisfies readonly SessionType[];

/** The 16 document types of the contract (`resource`), in the order the api lists them: a new one fails the typecheck here. */
export const RESOURCES = [
  "stenografico",
  "allegato_a",
  "allegato_b",
  "allegato_b_atti_modificati",
  "allegato_odg",
  "odg_giorno",
  "ordine_del_giorno",
  "resoconto_in_corso",
  "comunicato",
  "resoconto_sommario",
  "bollettino_pdf",
  "testo_atto",
  "dossier",
  "relazione",
  "emendamenti",
  "ordine_del_giorno_commissione",
] as const satisfies readonly Resource[];

const SOURCE_KEY = /^(orchestra|coro):[A-Za-z0-9_.-]{1,100}$/;
const FEED_ID = /^[A-Za-z0-9-]{1,64}$/;

function oneOf<T extends string>(raw: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.find((v) => v === raw) ?? fallback;
}

/** A comma-separated list: the valid values once each, in the order given; null when none is valid. */
function list<T extends string>(raw: string | null, valid: (value: string) => value is T): T[] | null {
  if (raw === null) return null;
  const values = raw
    .split(",")
    .map((v) => v.trim())
    .filter(valid);
  return values.length > 0 ? [...new Set(values)] : null;
}

const among =
  <T extends string>(allowed: readonly T[]) =>
  (value: string): value is T =>
    allowed.some((a) => a === value);
const matching =
  (pattern: RegExp) =>
  (value: string): value is string =>
    pattern.test(value);

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const isDay = (raw: string | null): raw is string =>
  raw !== null && ISO_DAY.test(raw) && !Number.isNaN(Date.parse(raw));

/** A word as Loggione compares it: lower case, no accents. */
export function foldWord(word: string): string {
  return word.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

const WORD = /[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu;

/**
 * The words of «Imposta plurali e varianti»: each word typed, once, as
 * typed (the excluded ones too, «-Energia»), not the ones in quotes or with
 * the asterisk, which are exact or a beginning. OR is not a word.
 */
/** The words the search leaves out as too common (Loggione's search/words.py, STOP_WORDS). */
export const STOP_WORDS = new Set(
  (
    "a agli ai al all alla alle allo con d da dagli dai dal dall dalla dalle dallo degli dei del dell della " +
    "delle dello di e ed fra gli i il in l la le lo negli nei nel nell nella nelle nello o per su sugli sui sul " +
    "sull sulla sulle sullo tra un una uno"
  ).split(" "),
);

/** The least letters of a beginning (legg*): Loggione refuses fewer (prefix-too-short). */
export const MIN_PREFIX = 4;

/** «Scegli la variante» of a word: its beginnings of 4 letters and more, up to the whole word (Legg*, Legge*). */
export function beginningsOf(word: string): string[] {
  const letters = [...word];
  const out: string[] = [];
  for (let n = MIN_PREFIX; n <= letters.length; n += 1) out.push(letters.slice(0, n).join(""));
  return out;
}

export type Keyword = { word: string; key: string; excluded: boolean; common: boolean; quoted: boolean };

/** A single word in quotes ("davide", -"davide"): «Solo «Davide»», still a word of the panel (3/11). */
function quotedWord(token: string): string | null {
  const bare = token.startsWith("-") ? token.slice(1) : token;
  if (!bare.startsWith('"') || !bare.endsWith('"') || bare.length < 3) return null;
  const words = bare.slice(1, -1).match(WORD) ?? [];
  return words.length === 1 && words[0] === bare.slice(1, -1) ? words[0] : null;
}

/**
 * The choices of the words still in `q`: a word deleted from the field takes its choices with it
 * (its plural, name, beginning), so the tags under the field say only what the search will do.
 */
export function withChoicesOfWords(p: SearchParams): SearchParams {
  const keys = new Set(keywordsOf(p.q).map((k) => k.key));
  return {
    ...p,
    variantsFor: p.variantsFor.filter((k) => keys.has(k)),
    namesFor: p.namesFor.filter((k) => keys.has(k)),
    noSynonymsFor: p.noSynonymsFor.filter((k) => keys.has(k)),
    prefixFor: Object.fromEntries(Object.entries(p.prefixFor).filter(([k]) => keys.has(k))),
  };
}

export function keywordsOf(q: string): Keyword[] {
  const out: Keyword[] = [];
  const seen = new Set<string>();
  for (const token of q.match(/-?"[^"]*(?:"|$)|[^\s"]+/g) ?? []) {
    const excluded = token.startsWith("-") && token.length > 1;
    const single = quotedWord(token);
    if (single !== null) {
      const key = foldWord(single);
      if (!seen.has(key)) {
        seen.add(key);
        out.push({ word: single, key, excluded, common: STOP_WORDS.has(key), quoted: true });
      }
      continue;
    }
    if (token.toUpperCase() === "OR" || token.toUpperCase() === "AND" || token.includes('"') || token.endsWith("*"))
      continue;
    for (const word of token.match(WORD) ?? []) {
      const key = foldWord(word);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ word, key, excluded, common: STOP_WORDS.has(key), quoted: false });
    }
  }
  return out.slice(0, 8);
}

/** The search with the quotes of one word taken away ("davide" → davide): another choice than «Solo» for it. */
export function withoutQuotes(q: string, key: string): string {
  return q.replace(/-?"[^"]*(?:"|$)|[^\s"]+/g, (token) => {
    const single = quotedWord(token);
    if (single === null || foldWord(single) !== key) return token;
    return `${token.startsWith("-") ? "-" : ""}${single}`;
  });
}

export function readSearchParams(search: URLSearchParams): SearchParams {
  const origin = search.get("origin");
  const feedId = search.get("feed_id")?.trim() ?? "";
  let period = oneOf(search.get("period"), PAGE_PERIODS, DEFAULT_SEARCH_PARAMS.period);
  const from = search.get("from");
  const to = search.get("to");
  // «Da… a…» needs both days, the first not after the second: otherwise the default period
  const range = period === "range" && isDay(from) && isDay(to) && from <= to;
  if (period === "range" && !range) period = DEFAULT_SEARCH_PARAMS.period;
  const q = search.get("q")?.trim() ?? "";
  let mode = oneOf(search.get("mode"), MODES, DEFAULT_SEARCH_PARAMS.mode);
  // an old link with the names for the whole search: every word a name, word by word now
  const namesFor =
    mode === "names"
      ? keywordsOf(q)
          .filter((k) => !k.common)
          .map((k) => k.key)
      : variantsIn(search.get("names"), q);
  if (mode === "names") mode = "synonyms";
  // one choice a word: the name first, then the beginning, then the plural
  const prefixFor = prefixesIn(search.get("prefixes"), q);
  for (const key of namesFor) delete prefixFor[key];
  const variantsFor = variantsIn(search.get("variants"), q).filter((k) => !namesFor.includes(k) && !prefixFor[k]);
  return {
    q,
    mode,
    words: oneOf(search.get("words"), WORDS, DEFAULT_SEARCH_PARAMS.words),
    variantsFor,
    prefixFor,
    namesFor,
    // the acronyms are always on (Davide and Luisa, 5/11): an old link's «nosyn» is not read
    noSynonymsFor: [],
    sort: oneOf(search.get("sort"), SORTS, DEFAULT_SEARCH_PARAMS.sort),
    period,
    dateFrom: range ? from : null,
    dateTo: range ? to : null,
    origin: ORIGINS.find((o) => o === origin) ?? null,
    followedOnly: search.get("followed") === "1",
    chamber: list(search.get("chamber"), among(CHAMBERS)),
    sessionType: list(search.get("session_type"), among(SESSION_TYPES)),
    resource: list(search.get("resource"), among(RESOURCES)),
    sources: list(search.get("sources"), matching(SOURCE_KEY)),
    feedId: FEED_ID.test(feedId) ? feedId : null,
  };
}

/** The words of `variants` that are words of the search, once each, in the order of the search. */
function variantsIn(raw: string | null, q: string): string[] {
  if (!raw) return [];
  const wanted = new Set(raw.split(",").map((w) => foldWord(w.trim())));
  return keywordsOf(q)
    .map((k) => k.key)
    .filter((key) => wanted.has(key));
}

/** The beginnings of `prefixes` that belong to a word of the search and have 4 letters at least. */
function prefixesIn(raw: string | null, q: string): Record<string, string> {
  if (!raw) return {};
  const words = new Set(keywordsOf(q).map((k) => k.key));
  const out: Record<string, string> = {};
  for (const pair of raw.split(",")) {
    const [word, start] = pair.split(":").map((p) => foldWord(p.trim()));
    if (word && start && words.has(word) && word.startsWith(start) && [...start].length >= MIN_PREFIX)
      out[word] = start;
  }
  return out;
}

/** The pairs word:beginning, in the order of the words of the search. */
function prefixPairs(p: Pick<SearchParams, "q" | "prefixFor">): string[] {
  return keywordsOf(p.q)
    .filter((k) => p.prefixFor[k.key])
    .map((k) => `${k.key}:${p.prefixFor[k.key]}`);
}

const nonEmpty = <T>(values: T[] | null): values is T[] => values !== null && values.length > 0;

/** The query string of the given state; defaults are left out, so the bare page stays /ricerca. */
export function writeSearchParams(params: Partial<SearchParams>): URLSearchParams {
  const p = { ...DEFAULT_SEARCH_PARAMS, ...params };
  const out = new URLSearchParams();
  if (p.q.trim()) out.set("q", p.q.trim());
  if (p.mode !== DEFAULT_SEARCH_PARAMS.mode) out.set("mode", p.mode);
  if (p.words !== DEFAULT_SEARCH_PARAMS.words) out.set("words", p.words);
  const variants = p.variantsFor.filter((key) => keywordsOf(p.q).some((k) => k.key === key));
  if (variants.length > 0) out.set("variants", variants.join(","));
  const prefixes = prefixPairs(p);
  if (prefixes.length > 0) out.set("prefixes", prefixes.join(","));
  const names = p.namesFor.filter((key) => keywordsOf(p.q).some((k) => k.key === key));
  if (names.length > 0) out.set("names", names.join(","));
  const plain = p.noSynonymsFor.filter((key) => keywordsOf(p.q).some((k) => k.key === key));
  if (plain.length > 0) out.set("nosyn", plain.join(","));
  if (p.sort !== DEFAULT_SEARCH_PARAMS.sort) out.set("sort", p.sort);
  if (p.period === "range") {
    if (p.dateFrom && p.dateTo) {
      out.set("period", "range");
      out.set("from", p.dateFrom);
      out.set("to", p.dateTo);
    }
  } else if (p.period !== DEFAULT_SEARCH_PARAMS.period) out.set("period", p.period);
  if (p.origin) out.set("origin", p.origin);
  if (p.followedOnly) out.set("followed", "1");
  if (nonEmpty(p.chamber)) out.set("chamber", p.chamber.join(","));
  if (nonEmpty(p.sessionType)) out.set("session_type", p.sessionType.join(","));
  if (nonEmpty(p.resource)) out.set("resource", p.resource.join(","));
  if (nonEmpty(p.sources)) out.set("sources", p.sources.join(","));
  if (p.feedId) out.set("feed_id", p.feedId);
  return out;
}

/** The current params with some of them changed: for a navigate. */
export function withSearchParams(search: URLSearchParams, change: Partial<SearchParams>): URLSearchParams {
  return writeSearchParams({ ...readSearchParams(search), ...change });
}

/** The path of a search: /ricerca with its query string. */
export function searchPath(params: Partial<SearchParams>): string {
  const query = writeSearchParams(params).toString();
  return query ? `/ricerca?${query}` : "/ricerca";
}

/**
 * The query of GET /search/v2 for the given state, without limit and offset:
 * the lists sorted (the same filters in another order are the same search),
 * the defaults sent as they are (the api has the same ones, but the page
 * does not depend on it). `followed_only` and `feed_id` never go together
 * (the contract refuses them): the feed wins, as a saved search would carry it.
 */
export function toSearchQuery(p: SearchParams): SearchQuery {
  const sorted = <T extends string>(values: T[]) => [...values].sort();
  return {
    q: p.q.trim(),
    mode: p.mode,
    words: p.words,
    ...(p.variantsFor.length > 0 && p.mode === "synonyms" ? { variants_for: [...p.variantsFor].sort() } : {}),
    // a beginning is for a word on its own: not with the names, not in «Frase esatta»
    ...(prefixPairs(p).length > 0 && p.mode !== "names" && p.words !== "phrase"
      ? { prefix_for: prefixPairs(p).sort() }
      : {}),
    ...(p.namesFor.length > 0 && p.mode === "synonyms" ? { names_for: [...p.namesFor].sort() } : {}),
    ...(p.noSynonymsFor.length > 0 ? { no_synonyms_for: [...p.noSynonymsFor].sort() } : {}),
    sort: p.sort,
    period: p.period,
    ...(p.period === "range" && p.dateFrom && p.dateTo ? { date_from: p.dateFrom, date_to: p.dateTo } : {}),
    ...(p.origin ? { origin: p.origin } : {}),
    ...(p.feedId ? { feed_id: p.feedId } : p.followedOnly ? { followed_only: true } : {}),
    ...(nonEmpty(p.chamber) ? { chamber: sorted(p.chamber) } : {}),
    ...(nonEmpty(p.sessionType) ? { session_type: sorted(p.sessionType) } : {}),
    ...(nonEmpty(p.resource) ? { resource: sorted(p.resource) } : {}),
    ...(nonEmpty(p.sources) ? { sources: sorted(p.sources) } : {}),
  };
}

/** The key of a search in the cache: its query, in a stable order. */
export function searchKey(query: SearchQuery): string {
  return JSON.stringify(Object.entries(query).sort(([a], [b]) => (a < b ? -1 : 1)));
}

/** Whether the search can be saved: a saved search keeps a period relative to today, never «Da… a…». */
export function canSave(p: SearchParams): boolean {
  return p.q.trim() !== "" && p.period !== "range";
}

/** What a saved search keeps of the page's state: everything but the results. Never called with «Da… a…» (canSave). */
export function toSavedQuery(p: SearchParams): SavedQuery {
  const query = toSearchQuery(p);
  if (p.period === "range") throw new Error("a saved search keeps a relative period");
  return {
    q: query.q,
    mode: p.mode,
    words: p.words,
    variants_for: query.variants_for ?? null,
    prefix_for: query.prefix_for ?? null,
    // only when there is a name: the saved searches of before keep their shape
    ...(query.names_for ? { names_for: query.names_for } : {}),
    ...(query.no_synonyms_for ? { no_synonyms_for: query.no_synonyms_for } : {}),
    sort: p.sort,
    period: p.period,
    origin: p.origin,
    followed_only: query.followed_only ?? false,
    feed_id: query.feed_id ?? null,
    chamber: query.chamber ?? null,
    session_type: query.session_type ?? null,
    resource: query.resource ?? null,
    sources: query.sources ?? null,
  };
}

/** The page's state of a saved search, as «Le mie ricerche» reopens it. */
export function fromSavedQuery(query: SavedQuery): SearchParams {
  // through the query string: a value the page does not know is dropped as in a typed URL
  return readSearchParams(
    writeSearchParams({
      q: query.q,
      mode: query.mode,
      words: query.words,
      variantsFor: query.variants_for ?? [],
      prefixFor: Object.fromEntries((query.prefix_for ?? []).map((pair) => pair.split(":") as [string, string])),
      namesFor: query.names_for ?? [],
      noSynonymsFor: [],
      sort: query.sort,
      period: query.period,
      origin: query.origin ?? null,
      followedOnly: query.followed_only,
      chamber: query.chamber ?? null,
      sessionType: query.session_type ?? null,
      resource: query.resource ?? null,
      sources: query.sources ?? null,
      feedId: query.feed_id ?? null,
    }),
  );
}

/**
 * «Mostra formula»: the search as one would type it by hand, with the syntax
 * the field takes (decisions 28 and 30). Every word that must be there is
 * joined by AND (a space says the same; AND is easier to read), the words of
 * which one is enough by OR, in brackets beside the others; «Frase esatta» is
 * the plain words in quotes, a plural the forms joined by OR (`forms`, as GET
 * /search/forms gives them; the word alone until they come), a beginning
 * legg*. A name is not in the formula: it is the choice «Nome proprio» of
 * the word, which «Usa questa formula» keeps (decision 29); the names
 * next to each other stay next to each other, one person (matteo salvini
 * AND manovra). What was typed with quotes, * and - stays as it is.
 */
export function formulaOf(
  p: SearchParams,
  forms: Record<string, string[]> = {},
  synonyms: Record<string, string[]> = {},
): string {
  const tokens = p.q.match(/-?"[^"]*(?:"|$)|[^\s"]+/g) ?? [];
  // the groups of the search: each a list of words of which one is enough
  const groups: string[][] = [];
  const excluded: string[] = [];
  let run: string[] = [];
  let joinNext = false;
  const push = (item: string | string[]) => {
    const words = Array.isArray(item) ? item : [item];
    if (joinNext && groups.length > 0) groups[groups.length - 1]!.push(...words);
    else groups.push([...words]);
    joinNext = false;
  };
  // a run of names is one person, written as typed (the choice of the words keeps it a name)
  let runOfNames = false;
  const flush = () => {
    if (run.length > 1) push(runOfNames ? run.join(" ") : `"${run.join(" ")}"`);
    // «Frase esatta» is exactly as written, a lone word too: "davide" (3/11)
    else if (run.length === 1) push(runOfNames ? run[0]! : `"${run[0]!}"`);
    run = [];
    runOfNames = false;
  };
  for (const token of tokens) {
    if (token.toUpperCase() === "AND") continue;
    if (token.toUpperCase() === "OR") {
      flush();
      joinNext = groups.length > 0;
      continue;
    }
    const negative = token.startsWith("-") && token.length > 1;
    const bare = negative ? token.slice(1) : token;
    const plain = !bare.includes('"') && !bare.endsWith("*");
    const key = foldWord(bare.replace(/[()]/g, ""));
    const isName = plain && p.namesFor.includes(key);
    // «Frase esatta»; and the names written together are one person (decision 32)
    if (plain && p.words === "phrase" && !negative) {
      run.push(bare);
      continue;
    }
    if (isName && !negative) {
      if (!runOfNames) flush();
      runOfNames = true;
      run.push(bare);
      continue;
    }
    flush();
    const sign = negative ? "-" : "";
    let item: string | string[] = token;
    // the synonyms of the lexicon, written out (ddl OR "disegno di legge"); taken off, the word in quotes
    const quoted = (f: string) => (f.includes(" ") ? `"${f}"` : f);
    const added = plain && !p.noSynonymsFor.includes(key) ? (synonyms[key] ?? []).map(quoted) : [];
    if (isName) item = token;
    else if (plain && p.prefixFor[key]) item = `${sign}${p.prefixFor[key]}*`;
    else if (plain && p.mode === "synonyms" && (p.variantsFor.includes(key) || added.length > 0)) {
      const all = [...(p.variantsFor.includes(key) && forms[key]?.length ? forms[key] : [bare]), ...added];
      if (negative) {
        excluded.push(...all.map((f) => `-${f}`));
        continue;
      }
      item = all;
    } else if (plain && p.noSynonymsFor.includes(key) && (synonyms[key]?.length ?? 0) > 0)
      // its synonyms taken off: in quotes, or the formula would bring them back ("ddl"). A word
      // without synonyms stays as typed: plain, it is already that word only (eolico OR eolici, 4/11)
      item = `${sign}"${bare}"`;
    if (negative) excluded.push(Array.isArray(item) ? item.join(" ") : item);
    else push(item);
  }
  flush();
  const positive = p.words === "any" ? [groups.flat()] : groups;
  const many = positive.length > 1;
  const written = positive
    .filter((g) => g.length > 0)
    .map((g) => (g.length > 1 && many ? `(${g.join(" OR ")})` : g.join(" OR ")));
  return [written.join(" AND "), ...excluded].filter(Boolean).join(" ");
}

/**
 * «Escludi questa parola» of «Impostazioni sulle singole parole»: the search with the
 * word left out (-Messina) or put back (Messina). The field says it, so the
 * field and the menu say the same thing.
 */
export function withExcluded(q: string, key: string, excluded: boolean): string {
  return q.replace(/-?"[^"]*(?:"|$)|[^\s"]+/g, (token) => {
    const single = quotedWord(token);
    if (single !== null) {
      if (foldWord(single) !== key) return token;
      const inner = token.startsWith("-") ? token.slice(1) : token;
      return excluded ? `-${inner}` : inner;
    }
    if (token === "OR" || token === "AND" || token.includes('"') || token.endsWith("*")) return token;
    const negative = token.startsWith("-") && token.length > 1;
    const bare = negative ? token.slice(1) : token;
    if (!(bare.match(WORD) ?? []).some((w) => foldWord(w) === key)) return token;
    return excluded ? `-${bare}` : bare;
  });
}

/**
 * The words of the search chosen as «Nome proprio», grouped as Loggione
 * groups them (decision 32): the named words typed next to each other are
 * one person (Matteo Salvini), a named word alone is a name; as typed.
 */
export function namedGroups(q: string, namesFor: readonly string[]): string[][] {
  const out: string[][] = [];
  let run: string[] = [];
  const flush = () => {
    if (run.length > 0) out.push(run);
    run = [];
  };
  for (const token of q.match(/-?"[^"]*(?:"|$)|[^\s"]+/g) ?? []) {
    const words = token.match(WORD) ?? [];
    const named =
      !token.startsWith("-") &&
      !token.includes('"') &&
      !token.endsWith("*") &&
      words.length === 1 &&
      namesFor.includes(foldWord(words[0]!));
    if (named) run.push(words[0]!);
    else {
      flush();
      if (token.startsWith("-") && words.length === 1 && namesFor.includes(foldWord(words[0]!))) out.push([words[0]!]);
    }
  }
  flush();
  return out;
}
